import { useState, type ReactElement } from "react";
import { HoverWords } from "../bar/hover-words.tsx";
import { GlyphButton } from "../../ui/kit/index.ts";
import { say } from "../../words.ts";
import type { MapPopout } from "./popout-host.ts";
import type { WorldMapStore } from "./store.ts";

/**
 * The Pop out control (P2.12-T18, ADR-100 decisions 29, 30 and 50), shared by the window's title bar and the float's
 * strip: pressed, it asks the browser for the window in the same turn as the click, which is the gesture both ways need,
 * and shows the map as popped out once a window has opened. When a plain window is refused the in-game map stays and the
 * control's name and hover text read "Pop-up blocked" until the next press.
 */

/**
 * The part of the store the control acts on.
 */
export type PopOutTarget = Pick<WorldMapStore, "popOut">;

/**
 * The Pop out control.
 *
 * @param props The `store` it acts on, the `popout` that opens the window, the button's `size` in logical pixels and the
 *   `name` it is found by.
 * @returns The button, in a wrapper that carries its hover text.
 */
export function PopOutButton({ store, popout, size, name }: { readonly store: PopOutTarget; readonly popout: MapPopout; readonly size: number; readonly name: string }): ReactElement {
  const [blocked, setBlocked] = useState(false);
  const words = blocked ? say("worldMap.popBlocked") : say("worldMap.popOut");
  return (
    <span className="ot-worldmap__pop">
      <HoverWords words={words}>
        <GlyphButton
          glyph="popout"
          label={words}
          size={size}
          keepFocus
          onClick={() => {
            void popout.open(() => store.popOut()).then((outcome) => setBlocked(outcome === "blocked"));
          }}
          data={{ "data-worldmap": name }}
        />
      </HoverWords>
    </span>
  );
}
